import { flow, map, slice, orderBy } from 'lodash/fp';
import { toLower } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

import { breakpoint } from '@zocdoc/components/src/grid';
import { PaddedContainer } from '@zocdoc/components/src/Layout';
import { Link } from '@zocdoc/components/src/link';
import { FontFamily } from '@zocdoc/components/src/typography';
import { filterToValidAttributes } from '@zocdoc/dom-property';

import { withAB } from '../../config/ab';
import { SHOW_SPECIALTY_FOOTER, VARIANT_ON } from '../../config/ab/experiments';
import { getThemeParameters } from '../../config/theme/selector';
import { withPopulatedSpecialties } from '../../gql/withPopulatedSpecialties';
import { withConditionalRender } from '../../Shared/withConditionalRender';

const generateSpecialtyHref = specialty => `/specialties/${specialty.id}`;

const getTopSpecialties = flow(
    orderBy('name', 'asc'),
    slice(0, 29),
    map(specialty => (
        <LinkWrapper key={specialty.id}>
            <StyledLink
                data-prod-guids={`footer-specialty-${toLower(specialty.name)}:[4c2b7f86]`}
                href={generateSpecialtyHref(specialty)}
            >
                {specialty.name}
            </StyledLink>
        </LinkWrapper>
    )),
);

class BaseTopPopulatedSpecialtyLinks extends React.PureComponent {
    static propTypes = {
        populatedSpecialties: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string,
                name: PropTypes.string,
                numberOfProviders: PropTypes.number,
            })
        ),

        directoryId: PropTypes.string,
    };

    static defaultProps = {
        populatedSpecialties: [],
    }

    render() {
        const { populatedSpecialties, ...props } = this.props;

        if (populatedSpecialties.length > 0) {
            return (
                <Container {...filterToValidAttributes(props)}>
                    <Header>
                    View all providers by type
                    </Header>

                    <Links>
                        {getTopSpecialties(populatedSpecialties)}
                        <LinkWrapper key="view-all">
                            <StyledLink
                                data-prod-guids="footer-specialties-all:[4c2b7f86]"
                                href="/specialties"
                            >
                                View all types
                            </StyledLink>
                        </LinkWrapper>
                    </Links>
                </Container>
            );
        } else {
            return <div />;
        }
    }
}

const Container = styled(PaddedContainer)`
    background-color: ${p => p.theme.footer.legal.bgColor};
    box-sizing: border-box;
    color: ${p => p.theme.footer.legal.textColor};
    padding-bottom: 35px;
    padding-top: 35px;
    position: relative;
`;

const Header = styled.h3`
    font: 18px ${FontFamily.SharpSansSemibold};
    margin-bottom: 32px;
`;

const Links = styled.nav`
    ${breakpoint.small} {
        column-count: 2;
        column-gap: 5vw;
    }

    ${breakpoint.medium} {
        column-count: 3;
    }
`;

const LinkWrapper = styled.div`
    margin-bottom: 10px;
`;

const StyledLink = styled(Link)`
    border-color: ${p => p.theme.footer.legal.linkUnderlineColor};
    color: ${p => p.theme.footer.legal.linkColor};
    line-height: 1.5;

    &:hover,
    &:focus {
        border-color: ${p => p.theme.footer.legal.linkUnderlineColorHover};
        color: ${p => p.theme.footer.legal.linkColorHover};
    }

    &:visited {
        border-color: ${p => p.theme.footer.legal.linkUnderlineColorVisited};
        color: ${p => p.theme.footer.legal.linkColorVisited};
    }
`;

const mapStateToProps = (state, props) => {
    const theme = getThemeParameters(state);

    return {
        directoryId: theme.directoryId,

        /**
         * used by withConditionalRender
         */
        render: theme.specialtyDirectory.enabled && props.ab[SHOW_SPECIALTY_FOOTER.experimentId] === VARIANT_ON,
    };
};

export const TopPopulatedSpecialtyLinks = (
    withAB(SHOW_SPECIALTY_FOOTER)(
        connect(mapStateToProps)(
            withConditionalRender(
                withPopulatedSpecialties(
                    BaseTopPopulatedSpecialtyLinks
                )
            )
        )
    )
);

